<template>
  <AppLayout>
    <div class="home">
      <section id="approvers-overview">
        <div class="container-fluid px-4">

          <!-- Page Header -->
          <div class="d-flex align-items-center justify-content-between mb-3 pt-2">
            <div>
              <h5 class="mb-0 fw-semibold">Approvers Overview</h5>
              <small class="text-muted">All configured approvers across departments and modules</small>
            </div>
            <button class="btn btn-sm btn-style-2" @click="loadData" :disabled="loading">
              <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i> Refresh
            </button>
          </div>

          <!-- Filters Row -->
          <div class="d-flex align-items-start gap-3 mb-3 flex-wrap">
            <div class="search-wrap">
              <i class="fas fa-search search-icon text-muted"></i>
              <input
                type="text"
                class="form-control ps-5"
                placeholder="Search department..."
                v-model="search"
              />
            </div>
            <div>
              <p class="mb-1 small text-muted fw-semibold">Show columns:</p>
              <div class="d-flex gap-2 flex-wrap">
                <button
                  v-for="col in COLUMNS"
                  :key="col.key"
                  class="btn btn-sm column-toggle"
                  :class="activeColumns.includes(col.key) ? 'btn-primary' : 'btn-style-2'"
                  @click="toggleColumn(col.key)"
                >
                  {{ col.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading approvers...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="alert alert-danger d-flex align-items-center gap-3">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ error }}</span>
            <button class="btn btn-sm btn-outline-danger ms-auto" @click="loadData">
              <i class="fas fa-redo me-1"></i> Retry
            </button>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loading && filteredRows.length === 0" class="text-center py-5 text-muted">
            <i class="fas fa-users fa-2x mb-3"></i>
            <p class="mb-0">No departments found{{ search ? ` matching "${search}"` : '' }}.</p>
          </div>

          <!-- Matrix Table -->
          <div v-else class="table-responsive">
            <table class="table table-bordered approvers-matrix">
              <thead>
                <tr>
                  <th class="dept-header-col">Department</th>
                  <th
                    v-for="col in visibleColumns"
                    :key="col.key"
                    class="module-header-col text-center"
                  >
                    <span class="module-label">{{ col.label }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredRows" :key="row.id">
                  <td class="dept-cell">
                    <span class="fw-semibold d-block">{{ row.name }}</span>
                    <small class="text-muted">
                      {{ row.ranks }} approval level{{ row.ranks !== 1 ? 's' : '' }}
                    </small>
                  </td>
                  <td
                    v-for="col in visibleColumns"
                    :key="col.key"
                    class="approver-cell"
                  >
                    <template v-if="getCellApprovers(row, col).length > 0">
                      <div
                        v-for="approver in getCellApprovers(row, col)"
                        :key="`${approver.userId}-${approver.role}`"
                        class="approver-pill"
                        @click="goToUser(approver.userId)"
                        :title="approver.email"
                      >
                        <span class="approver-avatar">{{ initials(approver.name) }}</span>
                        <span class="approver-info">
                          <span class="approver-name">{{ approver.name }}</span>
                          <span v-if="approver.role" class="approver-role">{{ approver.role }}</span>
                        </span>
                      </div>
                    </template>
                    <span v-else class="no-approver">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Legend -->
          <div v-if="!loading && !error && filteredRows.length > 0" class="legend mt-2 mb-4">
            <span class="legend-item">
              <span class="approver-avatar legend-avatar">AB</span>
              Click a user pill to view their profile
            </span>
            <span class="legend-separator">·</span>
            <span class="legend-item text-muted">
              "—" means no approver is assigned for that module in this department
            </span>
          </div>

        </div>
      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue'

const API_BASE = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')

const COLUMNS = [
  {
    key: 'purchase',
    label: 'Purchase',
    flags: [
      { flag: 'verifyPurchase', role: 'Verify' },
      { flag: 'stampPurchase', role: 'Stamp' },
      { flag: 'ceoStampPurchase', role: 'CEO Stamp' },
      { flag: 'approvePurchase', role: 'Approve' },
    ],
  },
  {
    key: 'dispatch',
    label: 'Dispatch',
    flags: [{ flag: 'approveDispatch', role: null }],
  },
  {
    key: 'pickup',
    label: 'Pickup',
    flags: [{ flag: 'approvePickup', role: null }],
  },
  {
    key: 'transfer',
    label: 'Stock Transfer',
    flags: [{ flag: 'approveTransfer', role: null }],
  },
  {
    key: 'adjustment',
    label: 'Stock Adjustment',
    flags: [{ flag: 'approveAdjustment', role: null }],
  },
  {
    key: 'bid',
    label: 'Bid Opportunity',
    flags: [{ flag: 'canApproveBidOpportunity', role: null }],
  },
]

export default {
  name: 'ApproversOverview',
  components: { AppLayout },

  data() {
    return {
      COLUMNS,
      departments: [],
      loading: false,
      error: null,
      search: '',
      activeColumns: COLUMNS.map(c => c.key),
    }
  },

  computed: {
    visibleColumns() {
      return COLUMNS.filter(c => this.activeColumns.includes(c.key))
    },
    filteredRows() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.departments
      return this.departments.filter(d => d.name.toLowerCase().includes(q))
    },
  },

  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      try {
        const token = JSON.parse(localStorage.getItem('access_token'))
        const headers = { 'Content-Type': 'application/json' }
        if (token) headers['Authorization'] = `Bearer ${token}`
        const res = await fetch(`${API_BASE}/departments/approvers`, { headers })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.message || `Server error ${res.status}`)
        }
        const data = await res.json()
        const list = Array.isArray(data) ? data : (data.data || data.departments || [])
        this.departments = list
      } catch (err) {
        this.error = err.message || 'Failed to load approvers. Please try again.'
      } finally {
        this.loading = false
      }
    },

    getCellApprovers(department, column) {
      const seen = new Set()
      const result = []

      for (const perm of department.permissions || []) {
        for (const { flag, role } of column.flags) {
          if (!perm[flag]) continue
          for (const user of perm.users || []) {
            const key = `${user.id}-${role}`
            if (seen.has(key)) continue
            seen.add(key)
            result.push({
              userId: user.id,
              name: user.name || user.email || 'Unknown',
              email: user.email || '',
              role: role,
            })
          }
        }
      }

      return result
    },

    toggleColumn(key) {
      if (this.activeColumns.includes(key)) {
        if (this.activeColumns.length === 1) return
        this.activeColumns = this.activeColumns.filter(k => k !== key)
      } else {
        const originalOrder = COLUMNS.map(c => c.key)
        this.activeColumns = originalOrder.filter(k => k === key || this.activeColumns.includes(k))
      }
    },

    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },

    goToUser(id) {
      if (id) this.$router.push(`/settings/users/${id}`)
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>

<style lang="scss" scoped>
#approvers-overview {
  padding-top: 10px;
}

.search-wrap {
  position: relative;
  min-width: 260px;

  .search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
  }
}

.column-toggle {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 20px;
  transition: all 0.15s;
}

/* Matrix Table */
.approvers-matrix {
  min-width: 700px;
  font-size: 11px;
  border-collapse: separate;
  border-spacing: 0;

  thead tr th {
    background-color: #f0f3fa;
    color: #0c275e;
    font-weight: 600;
    white-space: nowrap;
    border-bottom: 2px solid #d0d9ef;
    vertical-align: middle;
  }

  .dept-header-col {
    min-width: 160px;
    width: 160px;
    padding: 10px 13px;
  }

  .module-header-col {
    min-width: 140px;
    padding: 10px 11px;

    .module-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
  }

  tbody tr {
    transition: background 0.1s;

    &:hover {
      background-color: #f7f9ff;
    }
  }

  .dept-cell {
    padding: 10px 13px;
    vertical-align: middle;
    background-color: #fafbff;
    border-right: 2px solid #d0d9ef;
    min-width: 160px;
  }

  .approver-cell {
    padding: 8px 10px;
    vertical-align: top;
    min-width: 140px;
  }
}

/* Approver Pill */
.approver-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 8px;
  margin-bottom: 4px;
  border-radius: 8px;
  background-color: #eef1fb;
  border: 1px solid #d8dff5;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: #dce3f7;
    border-color: #b0bfe8;
  }
}

.approver-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #0c275e;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.approver-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
}

.approver-name {
  font-weight: 500;
  color: #0c275e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
}

.approver-role {
  font-size: 10px;
  color: #6c757d;
  font-style: italic;
}

.no-approver {
  color: #c0c7d6;
  font-size: 16px;
  display: block;
  text-align: center;
  padding: 4px 0;
}

/* Legend */
.legend {
  font-size: 11px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.legend-avatar {
  width: 20px;
  height: 20px;
  font-size: 8px;
}

.legend-separator {
  font-size: 18px;
  line-height: 1;
  color: #c0c7d6;
}
</style>
